const isNonEmptyText = (value) => typeof value === 'string' && value.trim().length > 0;

const hasMilestoneValue = (milestone = {}) =>
  isNonEmptyText(milestone.label) ||
  isNonEmptyText(milestone.value) ||
  Number.isFinite(Number(milestone.value));

const hasLevelData = (level = {}) => {
  if (!level || typeof level !== 'object') return false;
  return (
    isNonEmptyText(level.levelName) ||
    isNonEmptyText(level.tier) ||
    Number.isFinite(Number(level.points))
  );
};

const hasMatchSummaryData = (matchParticipation = {}) => {
  if (!matchParticipation || typeof matchParticipation !== 'object') return false;
  const summary = matchParticipation.summary || {};
  return Object.values(summary).some((item) => {
    if (typeof item === 'number') return item > 0;
    return isNonEmptyText(String(item || ''));
  });
};

const hasMediaItem = (media = {}) =>
  isNonEmptyText(media.url) || isNonEmptyText(media.title);

const SectionCard = ({ icon, title, children }) => (
  <div className="rounded-4 border bg-white p-3 p-md-4 shadow-sm">
    <div className="d-flex align-items-center gap-2 mb-3">
      <span className="fs-5">{icon}</span>
      <h6 className="mb-0">{title}</h6>
    </div>
    {children}
  </div>
);

const ProfileAddOnsView = ({
  addOns,
  highlights,
  achievements,
  milestones,
  awards,
  badges,
  level,
  matchParticipation,
  mediaGallery,
}) => {
  const enabledAddOns = addOns || {};

  const safeHighlights = Array.isArray(highlights)
    ? highlights.filter((item) => isNonEmptyText(item?.title))
    : [];
  const safeAchievements = Array.isArray(achievements)
    ? achievements.filter((item) => isNonEmptyText(item?.title))
    : [];
  const safeMilestones = Array.isArray(milestones)
    ? milestones.filter(hasMilestoneValue)
    : [];
  const safeAwards = Array.isArray(awards)
    ? awards.filter((item) => isNonEmptyText(item?.name))
    : [];
  const safeBadges = Array.isArray(badges)
    ? badges.filter((item) => isNonEmptyText(item?.label) || isNonEmptyText(item?.code))
    : [];
  const safeMediaGallery = Array.isArray(mediaGallery)
    ? mediaGallery.filter(hasMediaItem)
    : [];

  if (
    !enabledAddOns ||
    Object.values(enabledAddOns).every((v) => !v)
  ) {
    return null;
  }

  return (
    <div className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div className="small text-uppercase fw-semibold text-primary">
            Add-ons & Features
          </div>
          <div className="text-muted small">
            Extra profile sections enabled by this player
          </div>
        </div>
      </div>

      <div className="row g-3">
        {enabledAddOns.careerHighlightsEnabled && safeHighlights.length > 0 && (
          <div className="col-md-6">
            <SectionCard icon="✨" title="Career Highlights">
              <ul className="mb-0 small ps-3">
                {safeHighlights.map((item, index) => (
                  <li key={item.id || index}>{item.title}</li>
                ))}
              </ul>
            </SectionCard>
          </div>
        )}

        {enabledAddOns.achievementsEnabled && safeAchievements.length > 0 && (
          <div className="col-md-6">
            <SectionCard icon="🏆" title="Achievements">
              <ul className="mb-0 small ps-3">
                {safeAchievements.map((item, index) => (
                  <li key={item.id || index}>{item.title}</li>
                ))}
              </ul>
            </SectionCard>
          </div>
        )}

        {enabledAddOns.milestonesEnabled && safeMilestones.length > 0 && (
          <div className="col-md-6">
            <SectionCard icon="🎯" title="Milestones">
              <ul className="mb-0 small ps-3">
                {safeMilestones.map((item, index) => (
                  <li key={item.id || index}>
                    <strong>{item.label || 'Milestone'}</strong>
                    {item.value ? ` – ${item.value}` : ''}
                    {item.unit ? ` ${item.unit}` : ''}
                  </li>
                ))}
              </ul>
            </SectionCard>
          </div>
        )}

        {enabledAddOns.awardsEnabled && safeAwards.length > 0 && (
          <div className="col-md-6">
            <SectionCard icon="🥇" title="Awards">
              <ul className="mb-0 small ps-3">
                {safeAwards.map((item, index) => (
                  <li key={item.id || index}>{item.name}</li>
                ))}
              </ul>
            </SectionCard>
          </div>
        )}

        {enabledAddOns.playerBadgesEnabled && safeBadges.length > 0 && (
          <div className="col-md-6">
            <SectionCard icon="🏷️" title="Player Badges">
              <div className="d-flex flex-wrap gap-2">
                {safeBadges.map((item, index) => (
                  <span key={index} className="badge rounded-pill text-bg-light border">
                    {item.icon || '🏅'} {item.label || item.code}
                  </span>
                ))}
              </div>
            </SectionCard>
          </div>
        )}

        {enabledAddOns.levelBadgesEnabled && hasLevelData(level) && (
          <div className="col-md-6">
            <SectionCard icon="📈" title="Level Badge">
              <div className="fw-semibold">
                {level?.levelName || level?.tier || 'Level'}
                <span className="text-muted ms-2">
                  ({Number(level?.points) || 0} pts)
                </span>
              </div>
            </SectionCard>
          </div>
        )}

        {enabledAddOns.matchParticipationEnabled &&
          hasMatchSummaryData(matchParticipation) && (
            <div className="col-12">
              <SectionCard icon="🗓️" title="Match Participation">
                <div className="d-flex flex-wrap gap-3 small">
                  <span className="badge text-bg-light border">
                    Matches: {matchParticipation?.summary?.matches || 0}
                  </span>
                  <span className="badge text-bg-light border">
                    Runs: {matchParticipation?.summary?.runs || 0}
                  </span>
                  <span className="badge text-bg-light border">
                    Wickets: {matchParticipation?.summary?.wickets || 0}
                  </span>
                </div>
              </SectionCard>
            </div>
          )}

        {enabledAddOns.mediaEnabled && safeMediaGallery.length > 0 && (
  <div className="col-12">
    <SectionCard icon="📸" title="Media Gallery">
      <div className="row g-3">
        {safeMediaGallery.map((item, index) => {
          const isImage = item.type === 'image' || /\.(png|jpg|jpeg|webp)$/i.test(item.url || '');
          const isVideo = item.type === 'video' || /\.(mp4|mov|webm)$/i.test(item.url || '');

          return (
            <div className="col-6 col-md-4 col-lg-3" key={index}>
              <div className="media-card h-100 border rounded-4 overflow-hidden bg-white shadow-sm">
                <div
                  className="media-card__thumb d-flex align-items-center justify-content-center bg-light"
                  style={{ height: 140 }}
                >
                  {isImage ? (
                    <img
                      src={item.url}
                      alt={item.title || 'Media'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : isVideo ? (
                    <div className="text-center">
                      <div className="fs-2">🎥</div>
                      <div className="small text-muted">Video</div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="fs-2">🔗</div>
                      <div className="small text-muted">Link</div>
                    </div>
                  )}
                </div>

                <div className="p-2">
                  <div className="fw-semibold small text-truncate">
                    {item.title || 'Untitled media'}
                  </div>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="small text-primary text-decoration-none"
                  >
                    View
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  </div>
)}

        {(enabledAddOns.joinTeamsEnabled || enabledAddOns.joinTournamentsEnabled) && (
          <div className="col-12">
            <SectionCard icon="🤝" title="Open to Opportunities">
              <p className="mb-0 small text-muted">
                This player is open to{' '}
                {enabledAddOns.joinTeamsEnabled && enabledAddOns.joinTournamentsEnabled
                  ? 'team and tournament invites'
                  : enabledAddOns.joinTeamsEnabled
                  ? 'team invites'
                  : 'tournament invites'}
                .
              </p>
            </SectionCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileAddOnsView;
